// Koppen inklappen.
//
// Een notitie die een jaar meegaat wordt lang: "unifi-protect" heeft inmiddels acht kopjes en
// je scrollt langs zeven ervan om bij de achtste te komen. Een kop moet je dus kunnen
// dichtklappen, en dan verdwijnt alles eronder tot de volgende kop van gelijk of hoger niveau
// — precies de indeling die het overzicht in de zijbalk ook gebruikt.
//
// Wat níet gebeurt: er verandert niets aan de notitie. Ingeklapt is hoe jij er nú naar kijkt,
// geen eigenschap van de tekst. Het staat dus ook niet in de export, en — belangrijker — het
// dichtklappen van een kop zet de notitie niet op "gewijzigd", stuurt geen bericht naar de NAS
// en maakt geen nieuwe versie aan. Voor zoiets als opvouwen zou dat de verkeerde prijs zijn.
//
// Daarom leeft de stand in een ProseMirror-plugin en niet in het document: een lijstje met de
// plekken van de dichtgeklapte koppen, dat bij elke wijziging meeschuift met de tekst.

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Node as PmKnoop } from "@tiptap/pm/model";
import { ICOONPADEN } from "./icoonpaden.ts";

export const VOUWEN = new PluginKey<number[]>("vouwen");

/** Eén blok in de rij, met alleen wat nodig is om te weten waar een kop ophoudt. */
export type Rij = { kop: boolean; niveau: number };

/**
 * Tot welke rij een kop zijn tekst meeneemt: alles tot de volgende kop van gelijk of hoger
 * niveau. Een `##` sleept de `###`'en eronder dus mee, maar stopt bij de volgende `##`.
 *
 * Apart en zonder ProseMirror erin, want dit is de hele regel van deze functie en het is het
 * enige stuk dat je zonder scherm kunt beproeven.
 */
export function reiktTot(rijen: Rij[], index: number): number {
  const eigen = rijen[index]?.niveau ?? 0;
  let tot = index + 1;
  while (tot < rijen.length && !(rijen[tot].kop && rijen[tot].niveau <= eigen)) tot++;
  return tot;
}

type Blokje = { pos: number; knoop: PmKnoop; rij: Rij };

/** De blokken op het bovenste niveau, met hun plek in het document. */
function blokjes(doc: PmKnoop): Blokje[] {
  const uit: Blokje[] = [];
  doc.forEach((knoop, pos) => {
    const kop = knoop.type.name === "heading";
    uit.push({ pos, knoop, rij: { kop, niveau: kop ? Number(knoop.attrs.level ?? 1) : 0 } });
  });
  return uit;
}

export type Verborgen = { kop: number; van: number; tot: number; aantal: number };

/** Welke stukken er nu verstopt zijn, en onder welke kop. */
export function verborgenBereiken(doc: PmKnoop, dicht: number[]): Verborgen[] {
  if (dicht.length === 0) return [];
  const rij = blokjes(doc);
  const uit: Verborgen[] = [];
  for (const [index, blokje] of rij.entries()) {
    if (!blokje.rij.kop || !dicht.includes(blokje.pos)) continue;
    const tot = reiktTot(
      rij.map((b) => b.rij),
      index
    );
    if (tot === index + 1) continue; // een kop zonder iets eronder valt niets te verbergen
    const laatste = rij[tot - 1];
    uit.push({
      kop: blokje.pos,
      van: rij[index + 1].pos,
      tot: laatste.pos + laatste.knoop.nodeSize,
      aantal: tot - index - 1
    });
  }
  return uit;
}

function tekenIcoon(naam: "omlaag"): SVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  const pad = document.createElementNS("http://www.w3.org/2000/svg", "path");
  pad.setAttribute("d", ICOONPADEN[naam]);
  svg.appendChild(pad);
  return svg;
}

export const Vouwen = Extension.create({
  name: "vouwen",

  addCommands() {
    return {
      /** Alles weer openklappen — bijvoorbeeld als je in de notitie gaat zoeken. */
      vouwAllesOpen:
        () =>
        ({ state, dispatch }: any) => {
          const dicht = VOUWEN.getState(state) ?? [];
          if (dicht.length === 0) return false;
          if (dispatch) dispatch(state.tr.setMeta(VOUWEN, { allesOpen: true }));
          return true;
        }
    } as any;
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<number[]>({
        key: VOUWEN,

        state: {
          init: () => [],
          apply(tr, dicht, _oud, nieuw) {
            const bericht = tr.getMeta(VOUWEN) as { wissel?: number; allesOpen?: boolean } | undefined;
            if (bericht?.allesOpen) return [];

            // Verandert er iets ín een verstopt stuk, dan klapt die kop open. Dat gebeurt
            // eerder dan je denkt: staat de cursor aan het eind van een dichtgeklapte kop en
            // druk je op Delete, dan trekt de regel die je niet ziet omhoog. Zonder dit zou je
            // tekst wijzigen die niet op het scherm staat — en dat mag nooit.
            const geraakt = new Set<number>();
            if (tr.docChanged) {
              for (const bereik of verborgenBereiken(_oud.doc, dicht)) {
                for (const kaart of tr.mapping.maps) {
                  kaart.forEach((van, tot) => {
                    if (tot >= bereik.van && van <= bereik.tot) geraakt.add(bereik.kop);
                  });
                }
              }
            }

            // Meeschuiven met wat er getypt is; daarna alleen houden wat écht nog een kop is.
            // Dat laatste ruimt zichzelf op: haal je een kop weg of wordt het weer een alinea,
            // dan verdwijnt zijn vouwstand vanzelf in plaats van ergens anders op te duiken.
            let volgende = dicht.filter((pos) => !geraakt.has(pos)).map((pos) => tr.mapping.map(pos));
            if (bericht?.wissel !== undefined) {
              volgende = volgende.includes(bericht.wissel)
                ? volgende.filter((pos) => pos !== bericht.wissel)
                : [...volgende, bericht.wissel];
            }
            volgende = [...new Set(volgende)].filter((pos) => nieuw.doc.nodeAt(pos)?.type.name === "heading");

            // Komt de cursor in een verstopt stuk terecht — met de pijltjes, of doordat er
            // ergens iets werd ingevoegd — dan klapt die kop open. Anders sta je te typen in
            // tekst die je niet ziet, en dat is het ergste wat een vouwfunctie kan doen.
            const plek = nieuw.selection.from;
            const open = verborgenBereiken(nieuw.doc, volgende)
              .filter((bereik) => plek > bereik.van && plek < bereik.tot)
              .map((bereik) => bereik.kop);
            return open.length > 0 ? volgende.filter((pos) => !open.includes(pos)) : volgende;
          }
        },

        props: {
          decorations(staat) {
            const dicht = VOUWEN.getState(staat) ?? [];
            const rij = blokjes(staat.doc);
            const verborgen = verborgenBereiken(staat.doc, dicht);
            const versierselen: Decoration[] = [];

            for (const blokje of rij) {
              if (!blokje.rij.kop) continue;
              const isDicht = dicht.includes(blokje.pos);
              const eigen = verborgen.find((bereik) => bereik.kop === blokje.pos);
              const pos = blokje.pos;

              // Het knopje staat vóór de tekst van de kop, in de kantlijn.
              versierselen.push(
                Decoration.widget(
                  pos + 1,
                  (view) => {
                    const knop = document.createElement("button");
                    knop.type = "button";
                    knop.className = `vouwknop${isDicht ? " dicht" : ""}`;
                    knop.contentEditable = "false";
                    knop.setAttribute("aria-expanded", String(!isDicht));
                    knop.title = isDicht ? "Uitklappen" : "Inklappen";
                    knop.appendChild(tekenIcoon("omlaag"));
                    // `mousedown` tegenhouden: anders verspringt de cursor naar de kop en
                    // scrollt het scherm terwijl je alleen maar wilde opvouwen.
                    knop.addEventListener("mousedown", (e) => e.preventDefault());
                    knop.addEventListener("click", (e) => {
                      e.preventDefault();
                      view.dispatch(view.state.tr.setMeta(VOUWEN, { wissel: pos }));
                    });
                    return knop;
                  },
                  { side: -1, key: `vouw-${pos}-${isDicht}`, stopEvent: () => true }
                )
              );

              if (!eigen) continue;
              versierselen.push(
                Decoration.widget(
                  pos + blokje.knoop.nodeSize - 1,
                  () => {
                    const merk = document.createElement("span");
                    merk.className = "vouwtelling";
                    merk.contentEditable = "false";
                    merk.textContent = eigen.aantal === 1 ? "1 blok" : `${eigen.aantal} blokken`;
                    return merk;
                  },
                  { side: 1, key: `telling-${pos}-${eigen.aantal}`, stopEvent: () => true }
                )
              );
            }

            for (const bereik of verborgen) {
              for (const blokje of rij) {
                if (blokje.pos >= bereik.van && blokje.pos < bereik.tot) {
                  versierselen.push(
                    Decoration.node(blokje.pos, blokje.pos + blokje.knoop.nodeSize, { class: "ingeklapt" })
                  );
                }
              }
            }

            return DecorationSet.create(staat.doc, versierselen);
          }
        }
      })
    ];
  }
});
